"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Check, ChevronDown, Languages } from "lucide-react";
import clsx from "clsx";
import { useTransition, useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import {
  defaultLocale,
  isLocale,
  localeIsAvailableFor,
  locales,
  stripLocale,
  type Locale,
} from "@/i18n/config";

interface LanguageSwitcherProps {
  variant?: "desktop" | "mobile" | "inline";
  onOpenChange?: (open: boolean) => void;
}

const LOCALE_LABELS: Record<Locale, { label: string; short: string }> = {
  es: { label: "Español", short: "ES" },
  en: { label: "English", short: "EN" },
  pt: { label: "Português", short: "PT" },
};

export default function LanguageSwitcher({
  variant = "desktop",
  onOpenChange,
}: LanguageSwitcherProps) {
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("menuMobile");

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const segment = pathname.split("/")[1] ?? "";
  const currentLocale: Locale = isLocale(segment) ? segment : defaultLocale;
  const pathWithoutLocale = stripLocale(pathname);

  // El portugués sólo cubre las secciones de pasajero; en corporativo ni
  // siquiera se ofrece, para no mandar al usuario a una página a medio traducir.
  const available = locales.filter((locale) =>
    localeIsAvailableFor(locale, pathWithoutLocale),
  );

  const handleLocaleChange = (locale: Locale) => {
    if (locale === currentLocale) return;
    const newPath = `/${locale}${pathWithoutLocale === "/" ? "" : pathWithoutLocale}`;
    const query = searchParams.toString();
    startTransition(() => {
      router.push(query ? `${newPath}?${query}` : newPath);
    });
  };

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer click fuera del dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  // Notificar al header cuando el dropdown abre/cierra (solo desktop)
  useEffect(() => {
    if (variant === "desktop" && onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange, variant]);

  const current = LOCALE_LABELS[currentLocale];

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-3">
        {available.map((locale, i) => {
          const isActive = locale === currentLocale;
          return (
            <div key={locale} className="flex items-center gap-3">
              {i > 0 && <span className="w-px h-3 bg-white/20" aria-hidden />}
              <button
                onClick={() => handleLocaleChange(locale)}
                disabled={isPending}
                aria-current={isActive}
                title={LOCALE_LABELS[locale].label}
                className={clsx(
                  "text-xs font-semibold tracking-wide transition-colors duration-200 cursor-pointer",
                  isActive
                    ? "text-[var(--acento-nav)]"
                    : "text-[var(--blanco)]/60 hover:text-[var(--blanco)]",
                )}
              >
                {LOCALE_LABELS[locale].short}
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  if (variant === "mobile") {
    return (
      <div className="flex gap-2">
        {available.map((locale) => {
          const isActive = locale === currentLocale;
          return (
            <button
              key={locale}
              onClick={() => handleLocaleChange(locale)}
              disabled={isPending}
              aria-current={isActive}
              className={clsx(
                "flex-1 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2",
                isActive
                  ? "bg-[var(--acento-nav)] text-[var(--fondoGeneral-secciones)] shadow-md"
                  : "bg-white/10 text-white/70 hover:bg-white/20",
              )}
            >
              <span>{LOCALE_LABELS[locale].short}</span>
              {isActive && <Check className="w-4 h-4" />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isPending}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={clsx(
          "flex items-center gap-1.5 xl:gap-2 px-2.5 xl:px-3 py-2 rounded-xl border border-white/25 bg-white/10 backdrop-blur-sm text-white text-sm font-semibold tracking-wide shadow-sm transition-all duration-300 hover:bg-white/15 hover:border-white/45 active:scale-95 cursor-pointer",
          { "opacity-50 pointer-events-none": isPending },
        )}
      >
        <Languages className="w-4 h-4 text-white/85 shrink-0" />
        <span>{current.short}</span>
        {/* El chevron se cae en el modo compacto: el botón ya se entiende */}
        <ChevronDown
          className={clsx(
            "hidden xl:block w-4 h-4 text-white/70 transition-transform duration-300",
            { "rotate-180": isOpen },
          )}
        />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-auto min-w-[11rem] rounded-2xl border border-white/15 bg-[var(--fondoGeneral-secciones)]/95 backdrop-blur-xl shadow-2xl shadow-black/40 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="px-3 pt-3 pb-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40">
              {t("language")}
            </p>
          </div>
          <ul role="listbox" className="px-2 pb-2 space-y-1">
            {available.map((locale) => {
              const isActive = locale === currentLocale;
              return (
                <li key={locale}>
                  <button
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      handleLocaleChange(locale);
                      setIsOpen(false);
                    }}
                    className={clsx(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-[var(--acento-nav)]/15 text-[var(--acento-nav)] border border-[var(--acento-nav)]/40"
                        : "text-white/80 hover:bg-white/10 hover:text-white",
                    )}
                  >
                    <span className="text-xs font-bold text-white/40 w-6 text-left">
                      {LOCALE_LABELS[locale].short}
                    </span>
                    <span className="flex-1 text-left">
                      {LOCALE_LABELS[locale].label}
                    </span>
                    {isActive && (
                      <Check className="w-4 h-4 text-[var(--acento-nav)] shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
